/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { KeyboardDatePicker } from "@material-ui/pickers";
import moment from "moment";
import Papa from "papaparse";
import DownloadIcon from "@material-ui/icons/GetApp";
import { FormControl, Grid, MenuItem, Select, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from "@material-ui/core";
import { Button } from "components/haven-design/buttons";
import { reportQuery as ReportQuery, ReportName as ReportNameEnum } from "types/gql-types";
import { reportQuery } from "../query";
import moduleStyles from "../reports.module.css";

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean
  }
}

enum DatePreset {
  ThisMonth = "ThisMonth",
  LastMonth = "LastMonth",
  Last30Days = "Last30Days",
  LastYear = "LastYear",
  YTD = "YTD",
  Custom = "Custom",
  AllTime = "AllTime",
}

const reportTitles = {
  [ReportNameEnum.INTEREST_COUNT]: {
    title: "Applications Received",
    subtitle: "Active Applications Received",
  },
  [ReportNameEnum.INTEREST_DATE_MISMATCH]: {
    title: "Fraud Detection",
    subtitle: "Only Active Applications",
  },
  [ReportNameEnum.INTEREST_DOB]: {
    title: "Head of Household Age",
    subtitle: "Only Active Applications",
  },
  [ReportNameEnum.INTEREST_ETHNICITY]: {
    title: "HoH Ethnicity Demographics",
    subtitle: "Head of Household information",
  },
  [ReportNameEnum.INTEREST_RACE]: {
    title: "HoH Racial Demographics",
    subtitle: "Head of Household information",
  },
  [ReportNameEnum.INTEREST_GENDER]: {
    title: "HoH Gender Demographics",
    subtitle: "Only Active Applications for Head of Household",
  },
  [ReportNameEnum.INTEREST_SOURCE]: {
    title: "Application Sources",
    subtitle: "Active, Removed, and Moved-In Application Sources",
  },
  [ReportNameEnum.INTEREST_STATUS]: {
    title: "Application Move-In & Removals",
    subtitle: "Reinstatements not included",
  },
  [ReportNameEnum.USER_STATS]: {
    title: "Logins by User",
    subtitle: null,
  },
};

type QueryVarsType = { dateStart: number | null; dateEnd: number | null };

const dateOptions = [
  [DatePreset.AllTime, "All Time"],
  [DatePreset.ThisMonth, "This month"],
  [DatePreset.LastMonth, "Last month"],
  [DatePreset.Last30Days, "Last 30 days"],
  [DatePreset.LastYear, "Last year"],
  [DatePreset.YTD, "Year to date"],
  [DatePreset.Custom, "Custom date range"],
];
interface Props {
  reportName: ReportNameEnum;
  hideDateFilter?: boolean;
  includeTitle?:boolean;
  processRow?(row: string[]): string[];
}

type optionsType = {
  [key: string]: Date[] | null[]
};

const getPresetDates = (datePreset: string) : Date[] | null[] | null => {
  const availableOptions: optionsType = {
    [DatePreset.ThisMonth]: [moment().startOf("month").toDate(), moment().toDate()],
    [DatePreset.LastMonth]: [
      moment().subtract(1, "month").startOf("month").toDate(),
      moment().subtract(1, "month").endOf("month").toDate(),
    ],
    [DatePreset.Last30Days]: [
      moment().subtract(31, "days").startOf("day").toDate(),
      moment().toDate(),
    ],
    [DatePreset.YTD]: [moment().startOf("year").toDate(), moment().toDate()],
    [DatePreset.LastYear]: [
      moment().subtract(1, "year").startOf("year").toDate(),
      moment().subtract(1, "year").endOf("year").toDate(),
    ],
    [DatePreset.AllTime]: [null, null],
  };
  return availableOptions[datePreset] || null;
};

const getCSVTitle = (reportName: ReportNameEnum, start: Date | null, end: Date | null) => {
  const { title } = reportTitles[reportName];

  const startString = start ? moment(start).format("MM/DD/YYYY") : "";
  const endString = end ? moment(end).format("MM/DD/YYYY") : "";

  if (start && end) {
    return `${title} for ${startString} - ${endString}`;
  } else if (start) {
    return `${title} since ${startString}`;
  } else if (end) {
    return `${title} until ${endString}`;
  }

  return `${title} as of ${moment().format("MM/DD/YYYY")}`;
};

interface TableData {
  report: {columns: string[], records: string[][], footer: string[]}
}
type MapFuncType = ((row: string[]) => string[]);


const renderTable = (
  data?: TableData,
  loading?: boolean,
  processRow?: MapFuncType,
) => {

  const rowData = React.useMemo(() => {
    if (data && data.report) {
      return processRow ? data.report.records.map(processRow) : data.report.records;
    }
    return [];

  }, [data]);
  return (
    <Table>
      <TableHead>
        <TableRow>{data && data.report.columns.map((label: string, i: number) => (
          <TableCell key={i}>{label}</TableCell>))}
        </TableRow>
      </TableHead>

      {loading ? (
        <TableBody>
          <TableRow>
            <TableCell style={{ border: "none" }}>Loading...</TableCell>
          </TableRow>
        </TableBody>
      ) : (
        <>
          <TableBody>
            {rowData.map((row: string[], i: number) => (
              <TableRow key={i}>
                {row.map((value: string, j: number) => (
                  <TableCell key={j}>{value}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>

          <TableFooter>
            <TableRow>{data && data.report.footer.map((value: string, i: number) => (
              <TableCell key={i}>{value}</TableCell>))}
            </TableRow>
          </TableFooter>
        </>
      )}
    </Table>
  );
};

export const ReportTable: React.FC<Props> = (props: Props) => {
  const [dateStart, setDateStart] = useState<Date | null>(null);
  const [dateEnd, setDateEnd] = useState<Date | null>(null);
  const [selectValue, setSelectValue] = useState(DatePreset.AllTime);
  const [queryVars, setQueryVars] = useState<QueryVarsType>({ dateStart: null, dateEnd: null });
  const opts = {
    fetchPolicy: "cache-and-network" as const,
    variables: { ...queryVars, name: props.reportName },
  };
  const { data, error, loading } = useQuery<ReportQuery>(reportQuery, opts);
  const titles = reportTitles[props.reportName];

  if (error || (!loading && !data)) return <div>Error loading table data</div>;

  const exportCSV = () => {
    if (!data) return;

    const title = getCSVTitle(props.reportName, dateStart, dateEnd);
    const { subtitle } = reportTitles[props.reportName];
    const csv = `${title}\n${subtitle ? `${subtitle}\n` : ""}\n${data.report.columns.join(",")}\n${Papa.unparse(data.report.records)}`;
    const csvData = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const filename = `${title.replace("/", "_")}.csv`;

    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(csvData, filename);
    } else {
      const tempLink = document.createElement("a");
      tempLink.href = window.URL.createObjectURL(csvData);
      tempLink.setAttribute("download", filename);
      tempLink.click();
    }
  };

  return (
    <Grid container spacing={2}>
      {props.includeTitle && (
        <Grid item xs={12}>
          <h1>{titles.title}</h1>
          <div className={moduleStyles.explainer}>{titles.subtitle}</div>
        </Grid>
      )}
      <Grid container spacing={2} alignContent="flex-end" justifyContent="flex-end">
        {!props.hideDateFilter && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <Select
                  variant="outlined"
                  margin="dense"
                  value={selectValue}
                  data-track="select-date-range"
                  onChange={(e) => {
                    const datePreset = e.target.value as DatePreset;

                    setSelectValue(datePreset);
                    const presetDates = getPresetDates(datePreset);
                    if (presetDates) {
                      const [start, end] = presetDates;
                      setDateStart(start);
                      setDateEnd(end);
                    }
                  }}
                >
                  {dateOptions.map(([val, key]) => (
                    <MenuItem key={`option-${val}`} value={val}>{key}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} container spacing={0}>
              <Grid item xs>
                <KeyboardDatePicker
                  label="From"
                  margin="dense"
                  disableFuture
                  required
                  autoComplete="off"
                  autoOk
                  variant="inline"
                  inputVariant="outlined"
                  format="MM/dd/yyyy"
                  InputAdornmentProps={{ position: "end" }}
                  value={dateStart}
                  onChange={(date) => {
                    setSelectValue(DatePreset.Custom);
                    setDateStart(date ? moment(date).startOf("day").toDate() : null);
                  }}
                />
              </Grid>
              <Grid item xs={1} style={{ alignSelf: "center" }}>
              &nbsp;&nbsp;—
              </Grid>
              <Grid item xs>
                <KeyboardDatePicker
                  label="To"
                  margin="dense"
                  disableFuture
                  required
                  autoComplete="off"
                  autoOk
                  variant="inline"
                  inputVariant="outlined"
                  format="MM/dd/yyyy"
                  InputAdornmentProps={{ position: "end" }}
                  name="temp-input" // used to omit field when parsing form
                  value={dateEnd}
                  onChange={(date) => {
                    setSelectValue(DatePreset.Custom);
                    setDateEnd(date ? moment(date).startOf("day").toDate() : null);
                  }}
                />
              </Grid>
            </Grid>
            <Grid item xs={6} sm={4} md style={{ alignSelf: "center" }}>
              {/* <FormControl margin="dense"> */}
              <Button
                style={{ marginRight: 8 }}
                color="primary"
                size="S"
                fullWidth
                onClick={() => setQueryVars({
                  dateStart: dateStart ? moment(dateStart).unix() : null,
                  dateEnd: dateEnd ? moment(dateEnd).unix() : null,
                })
                }
              >
            Run Report
              </Button>
              {/* </FormControl> */}
            </Grid>
          </>
        )}
        <Grid item xs={6} sm={4} md={props.hideDateFilter ? 2 : true} style={{ alignSelf: "center" }}>
          <Button
            fullWidth
            color="secondary"
            size="S"
            onClick={() => exportCSV()}
            disabled={!data || data.report.records.length === 0}
          >
            <DownloadIcon style={{ width: 13, height: 13, marginBottom: -2 }} /> Export
          </Button>
        </Grid>
      </Grid>
      <div style={{ width: "100%" }}>
        <div style={{ overflowX: "auto" }}>
          {renderTable(data, loading, props.processRow)}
        </div>
      </div>
    </Grid>
  );
};
